import React from "react";
import { Link } from "react-router-dom";

const summaryItems = [
  ["Active Programs", "active_programs"],
  ["Proposals awaiting Board", "proposals_awaiting_board"],
  ["Decisions awaiting Program", "recorded_decisions_awaiting_program"],
  ["Execution projects at risk", "execution_projects_at_risk"],
  ["Programs with effective Benefits Assessment", "programs_with_effective_benefits_assessment"],
  ["Programs in reauthorization", "programs_in_reauthorization"],
  ["Governance attention", "attention_count"],
];

const capitalItems = [
  ["Authorized Capital", "authorized_amount"],
  ["Original Commitment", "original_commitment_amount"],
  ["Released Capital", "released_amount"],
  ["Committed Capital", "committed_amount"],
  ["Available Capacity", "available_amount"],
];

const shown = (value) => value !== null && value !== undefined;
const words = (value) => value ? String(value).toLowerCase().replaceAll("_", " ")
  .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase()) : null;

function ManagementReassessment({ thread }) {
  const reassessment = thread.reporting_reassessment;
  if (reassessment?.status !== "AVAILABLE") return null;
  const conclusion = reassessment.continued_investment_conclusion
    || reassessment.management_recommendation;
  return (
    <article className="rounded-lg border border-slate-200 p-3" data-testid="management-reassessment">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold text-slate-900">Management Reassessment</h4>
          <p className="mt-1 text-sm text-slate-700">
            {words(reassessment.type)} reassessment
            {reassessment.assessment_date ? ` · ${reassessment.assessment_date}` : ""}
          </p>
          <p className="mt-1 break-all font-mono text-xs text-slate-500">{reassessment.reference}</p>
        </div>
        <Link className="text-sm font-semibold text-blue-700 hover:underline" to={`/benefits-governance/${thread.program.reference}`}>
          Open Benefits Governance
        </Link>
      </div>
      <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
        <div><dt className="text-slate-500">Management conclusion</dt><dd className="font-medium text-slate-900">{words(conclusion) || "No conclusion recorded"}</dd></div>
        <div><dt className="text-slate-500">Value Realization evidence considered</dt><dd className="break-all font-mono text-slate-900">{reassessment.adopted_value_realization_assessment_reference || "No Value Realization assessment adopted"}</dd></div>
        {reassessment.material_change_assessment && <div><dt className="text-slate-500">Material change assessment</dt><dd className="text-slate-900">{words(reassessment.material_change_assessment)}</dd></div>}
      </dl>
    </article>
  );
}

function CapitalPosition({ position, compact = false }) {
  if (position?.status === "INCOMPATIBLE_CURRENCIES") {
    return <p className="text-sm text-slate-600">Capital positions use incompatible source currencies.</p>;
  }
  if (position?.status !== "AVAILABLE") {
    return <p className="text-sm text-slate-600">Capital position unavailable across programs.</p>;
  }
  return (
    <dl className={`grid gap-3 ${compact ? "sm:grid-cols-3" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
      {capitalItems.map(([label, key]) => shown(position[key]) && (
        <div key={key} className="rounded-lg bg-slate-50 px-3 py-2">
          <dt className="text-xs font-medium text-slate-500">{label}</dt>
          <dd className="mt-1 font-mono text-sm font-semibold text-slate-900">{position[key]}</dd>
        </div>
      ))}
      {shown(position.currency) && <div className="rounded-lg bg-slate-50 px-3 py-2"><dt className="text-xs font-medium text-slate-500">Source Currency</dt><dd className="mt-1 font-mono text-sm font-semibold text-slate-900">{position.currency}</dd></div>}
    </dl>
  );
}

export default function InvestmentGovernanceEvidence({ engine }) {
  if (engine?.status === "loading") return <section aria-live="polite" className="rounded-xl border border-slate-200 bg-white p-5"><h2 className="font-semibold text-slate-950">Investment Governance</h2><p className="mt-2 text-sm text-slate-600">Loading authoritative governance evidence…</p></section>;
  if (engine?.status !== "ready" || !engine.data) {
    const message = engine?.reason === "FORBIDDEN"
      ? "Authoritative governance evidence is unavailable to your access level."
      : engine?.reason === "UNSUPPORTED_SCHEMA"
        ? "Authoritative governance evidence uses an unsupported schema."
        : "Authoritative governance evidence is currently unavailable.";
    return <section className="rounded-xl border border-slate-200 bg-white p-5"><h2 className="font-semibold text-slate-950">Investment Governance</h2><p className="mt-2 text-sm text-slate-600">{message}</p></section>;
  }

  const evidence = engine.data;
  const summary = evidence.position.summary || {};
  const programs = evidence.activity.threads.filter((thread) => thread?.thread_type === "PROGRAM");
  return (
    <section aria-labelledby="investment-governance-evidence" className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="investment-governance-evidence" className="text-lg font-semibold text-slate-950">Investment Governance</h2>
        <p className="text-xs text-slate-500">Governance evidence as of {evidence.source.generatedAt || "an authoritative snapshot"}</p>
      </div>
      <p className="mt-1 text-sm text-slate-600">Read-only organization position supplied by Investment Governance.</p>
      <dl className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {summaryItems.map(([label, key]) => shown(summary[key]) && <div key={key}><dt className="text-xs text-slate-500">{label}</dt><dd className="text-lg font-semibold text-slate-950">{summary[key]}</dd></div>)}
      </dl>
      <div className="mt-5 border-t border-slate-200 pt-4"><h3 className="mb-3 text-sm font-semibold text-slate-900">Capital Governance position</h3><CapitalPosition position={evidence.capitalPosition} /></div>
      {programs.some((thread) => thread.capital_position) && <div className="mt-5 border-t border-slate-200 pt-4"><h3 className="text-sm font-semibold text-slate-900">Program capital positions</h3><div className="mt-3 space-y-3">{programs.filter((thread) => thread.capital_position).map((thread) => <article key={thread.thread_reference} className="rounded-lg border border-slate-200 p-3"><p className="mb-2 text-sm font-medium text-slate-900">{thread.program?.name || thread.thread_reference}</p><CapitalPosition compact position={thread.capital_position} /></article>)}</div></div>}
      {programs.some((thread) => thread.reporting_reassessment?.status === "AVAILABLE") && <div className="mt-5 border-t border-slate-200 pt-4"><h3 className="text-sm font-semibold text-slate-900">Evidence-to-governance lineage</h3><div className="mt-3 space-y-3">{programs.map((thread) => <ManagementReassessment key={thread.thread_reference} thread={thread} />)}</div></div>}
      <div className="mt-5 border-t border-slate-200 pt-4 text-sm text-slate-600"><span className="font-medium text-slate-900">Governance attention:</span> {evidence.attention.items.length} item(s) · {evidence.attention.actionSummary?.total ?? 0} governed action(s)</div>
    </section>
  );
}
